<template>
  <div class="ia-root">

    <!-- ══════════════════ MODE RECHERCHE ══════════════════ -->
    <template v-if="!manualMode">

      <div class="ia-field">
        <div
          ref="inputWrapper"
          class="ia-input-wrap"
          :class="{ 'is-focused': isFocused, 'is-filled': selected }"
        >
          <div class="ia-input-icon">
            <i class="fas fa-map-marker-alt"></i>
          </div>
          <input
            ref="input"
            v-model="query"
            @input="onInput"
            @focus="isFocused = true"
            @blur="onBlur"
            @keydown.down.prevent="moveDown"
            @keydown.up.prevent="moveUp"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.escape="closeSuggestions"
            type="text"
            autocomplete="off"
            spellcheck="false"
            :placeholder="placeholder"
            class="ia-input"
          />
          <div class="ia-input-actions">
            <span v-if="loading" class="ia-spinner"></span>
            <button
              v-if="selected && !loading"
              @mousedown.prevent="clear"
              class="ia-clear-btn"
              title="Effacer"
              type="button"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <p v-if="error" class="ia-error">
          <i class="fas fa-exclamation-circle"></i> {{ error }}
        </p>
      </div>

      <!-- Dropdown -->
      <transition name="ia-drop">
        <ul
          v-if="suggestions.length > 0"
          class="ia-dropdown"
          :style="dropdownStyle"
          role="listbox"
          @mousedown.prevent
        >
          <li
            v-for="(item, index) in suggestions"
            :key="index"
            @mousedown.prevent="selectAddress(item)"
            @mouseover="highlighted = index"
            :class="['ia-option', { 'is-active': highlighted === index }]"
            role="option"
          >
            <div class="ia-option-pin">
              <i class="fas fa-map-pin"></i>
            </div>
            <div class="ia-option-text">
              <span class="ia-option-main">{{ item._label.split(',')[0] }}</span>
              <span class="ia-option-sub">{{ item._label.split(',').slice(1).join(',').trim() }}</span>
            </div>
          </li>
        </ul>
      </transition>

      <!-- Récap adresse sélectionnée -->
      <transition name="ia-reveal">
        <div v-if="selected && showRecap" class="ia-recap">
          <div class="ia-recap-row ia-recap-full">
            <span class="ia-recap-label">Rue</span>
            <span class="ia-recap-value">{{ dataModel.adresse1 }}</span>
          </div>
          <div class="ia-recap-divider"></div>
          <div class="ia-recap-row">
            <span class="ia-recap-label">Code postal</span>
            <span class="ia-recap-value">{{ dataModel.code_postal }}</span>
          </div>
          <div class="ia-recap-row">
            <span class="ia-recap-label">Ville</span>
            <span class="ia-recap-value">{{ dataModel.ville }}</span>
          </div>
        </div>
      </transition>

      <div class="ia-manual-link-wrap">
        <button type="button" @click="switchToManual" class="ia-link">
          <i class="fas fa-pen"></i>
          Adresse introuvable ? Saisir manuellement
        </button>
      </div>

    </template>

    <!-- ══════════════════ MODE MANUEL ══════════════════ -->
    <template v-else>
      <div class="ia-manual-header">
        <div class="ia-label">
          <span class="ia-label-dot"></span>
          Saisie manuelle
        </div>
        <button type="button" @click="switchToSearch" class="ia-link">
          <i class="fas fa-search"></i>
          Retour à la recherche
        </button>
      </div>

      <div class="ia-manual-grid">
        <div class="ia-field ia-field--full">
          <label class="ia-label-sm">Adresse</label>
          <div class="ia-input-wrap">
            <input
              v-model="dataModel.adresse1"
              @input="emitUpdate"
              type="text"
              placeholder="12 rue des Fleurs"
              class="ia-input ia-input--plain"
            />
          </div>
        </div>

        <div class="ia-field ia-field--full">
          <label class="ia-label-sm">
            Complément <span class="ia-optional">optionnel</span>
          </label>
          <div class="ia-input-wrap">
            <input
              v-model="dataModel.adresse2"
              @input="emitUpdate"
              type="text"
              placeholder="Bâtiment B, lieu-dit…"
              class="ia-input ia-input--plain"
            />
          </div>
        </div>

        <div class="ia-field">
          <label class="ia-label-sm">Code postal</label>
          <div class="ia-input-wrap">
            <input
              v-model="dataModel.code_postal"
              @input="emitUpdate"
              type="text"
              placeholder="69000"
              maxlength="5"
              class="ia-input ia-input--plain"
            />
          </div>
        </div>

        <div class="ia-field">
          <label class="ia-label-sm">Ville</label>
          <div class="ia-input-wrap">
            <input
              v-model="dataModel.ville"
              @input="emitUpdate"
              type="text"
              placeholder="Lyon"
              class="ia-input ia-input--plain"
            />
          </div>
        </div>
      </div>

      <div class="ia-notice">
        <i class="fas fa-info-circle"></i>
        Coordonnées GPS non disponibles en saisie manuelle.
      </div>
    </template>

  </div>
</template>

<script>
const MAPBOX_TOKEN = 'pk.eyJ1IjoidmljdG9yYW53IiwiYSI6ImNrajhuam1iOTZ1dnQydnFqbzRldDJrdW8ifQ.VkIKVhh5_Pcp7PxqQ-GCCg'
const GOUV_URL     = 'https://api-adresse.data.gouv.fr/search/'
const MAPBOX_URL   = 'https://api.mapbox.com/geocoding/v5/mapbox.places/'
const TIMEOUT_MS   = 3000

function fetchWithTimeout(url, ms = TIMEOUT_MS) {
  const ctrl  = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), ms)
  return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(timer))
}

function emptyModel() {
  return {
    adresse1    : '',
    adresse2    : '',
    code_postal : '',
    ville       : '',
    pays        : 'France',
    latitude    : null,
    longitude   : null,
  }
}

export default {
  name: 'InputAdresse',

  props: {
    // v-model
    value: {
      type    : Object,
      default : () => ({}),
    },
    // Afficher le récap après sélection
    showRecap: {
      type    : Boolean,
      default : true,
    },
    placeholder: {
      type    : String,
      default : '12 rue des Fleurs, Lyon…',
    },
  },

  data() {
    return {
      query         : '',
      suggestions   : [],
      highlighted   : -1,
      loading       : false,
      error         : '',
      selected      : false,
      manualMode    : false,
      isFocused     : false,
      debounceTimer : null,
      dropdownStyle : {},
      dataModel     : { ...emptyModel(), ...this.value },
    }
  },

  mounted() {
    // Pré-remplir si value fourni
    if (this.value?.adresse1) {
      this.query    = this.value.adresse1
      this.selected = true
    }
    window.addEventListener('resize', this.updateDropdownPosition)
    window.addEventListener('scroll', this.updateDropdownPosition, true)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateDropdownPosition)
    window.removeEventListener('scroll', this.updateDropdownPosition, true)
  },

  watch: {
    value: {
      deep      : true,
      immediate : false,
      handler(v) {
        if (JSON.stringify(v) !== JSON.stringify(this.dataModel)) {
          this.dataModel = { ...emptyModel(), ...v }
          if (v?.adresse1 && !this.manualMode) {
            this.query    = v.adresse1
            this.selected = true
          }
        }
      },
    },
  },

  methods: {

    /* ── Position dropdown ── */
    updateDropdownPosition() {
      const el = this.$refs.inputWrapper
      if (!el || this.suggestions.length === 0) return
      const rect = el.getBoundingClientRect()
      this.dropdownStyle = {
        top   : rect.bottom + 4 + 'px',
        left  : rect.left   + 'px',
        width : rect.width  + 'px',
      }
    },

    /* ── Recherche ── */
    onInput() {
      this.selected = false
      this.error    = ''
      clearTimeout(this.debounceTimer)
      if (!this.query || this.query.length < 3) {
        this.suggestions = []
        return
      }
      this.debounceTimer = setTimeout(() => this.search(), 300)
    },

    async search() {
      this.loading     = true
      this.suggestions = []
      try {
        const results = await this.searchGouv(this.query)
        if (results.length > 0) {
          this.suggestions = results
          this.highlighted = 0
          this.$nextTick(this.updateDropdownPosition)
          return
        }
        throw new Error('no_results')
      } catch {
        try {
          const fallback = await this.searchMapbox(this.query)
          this.suggestions = fallback
          this.highlighted = fallback.length > 0 ? 0 : -1
          if (fallback.length === 0) this.error = 'Aucune adresse trouvée.'
          else this.$nextTick(this.updateDropdownPosition)
        } catch {
          this.error = 'Impossible de joindre le service de géocodage.'
        }
      } finally {
        this.loading = false
      }
    },

    async searchGouv(q) {
      const url = `${GOUV_URL}?q=${encodeURIComponent(q)}&limit=5&type=housenumber`
      const res = await fetchWithTimeout(url)
      if (!res.ok) throw new Error('gouv_error')
      const data = await res.json()
      return (data.features || []).map(f => ({
        _source : 'gouv',
        _label  : f.properties.label,
        _props  : f.properties,
        _coords : f.geometry.coordinates,
      }))
    },

    async searchMapbox(q) {
      const url = `${MAPBOX_URL}${encodeURIComponent(q)}.json?autocomplete=true&limit=5&country=fr&types=address&access_token=${MAPBOX_TOKEN}`
      const res = await fetchWithTimeout(url)
      if (!res.ok) throw new Error('mapbox_error')
      const data = await res.json()
      return (data.features || []).map(f => ({
        _source : 'mapbox',
        _label  : f.place_name,
        _raw    : f,
      }))
    },

    /* ── Sélection ── */
    selectAddress(item) {
      if (item._source === 'gouv') {
        const p = item._props
        this.dataModel.adresse1    = p.name
        this.dataModel.code_postal = p.postcode
        this.dataModel.ville       = p.city
        this.dataModel.pays        = 'France'
        this.dataModel.longitude   = item._coords[0]
        this.dataModel.latitude    = item._coords[1]
      } else {
        const f   = item._raw
        const ctx = f.context || []
        this.dataModel.adresse1    = f.address ? `${f.address} ${f.text}` : f.place_name
        this.dataModel.code_postal = ctx.find(c => c.id.includes('postcode'))?.text || ''
        this.dataModel.ville       = ctx.find(c => c.id.includes('place'))?.text    || ''
        this.dataModel.pays        = ctx.find(c => c.id.includes('country'))?.text  || 'France'
        this.dataModel.longitude   = f.center[0]
        this.dataModel.latitude    = f.center[1]
      }

      this.query       = item._label
      this.suggestions = []
      this.highlighted = -1
      this.selected    = true
      this.isFocused   = false

      this.emitUpdate()
      this.$nextTick(() => this.$emit('address-selected', { ...this.dataModel }))
    },

    clear() {
      this.query       = ''
      this.selected    = false
      this.suggestions = []
      this.error       = ''
      this.dataModel   = emptyModel()
      this.emitUpdate()
      this.$nextTick(() => this.$refs.input?.focus())
    },

    moveDown()          { this.highlighted = Math.min(this.highlighted + 1, this.suggestions.length - 1) },
    moveUp()            { this.highlighted = Math.max(this.highlighted - 1, 0) },
    selectHighlighted() { if (this.highlighted >= 0) this.selectAddress(this.suggestions[this.highlighted]) },
    closeSuggestions()  { this.suggestions = []; this.highlighted = -1 },
    onBlur()            { this.isFocused = false; setTimeout(() => this.closeSuggestions(), 150) },

    /* ── Mode manuel ── */
    switchToManual() {
      if (!this.selected) this.dataModel.adresse1 = this.query
      this.dataModel.latitude  = null
      this.dataModel.longitude = null
      this.manualMode = true
      this.emitUpdate()
    },

    switchToSearch() {
      this.manualMode  = false
      this.selected    = false
      this.query       = ''
      this.dataModel   = emptyModel()
      this.emitUpdate()
      this.$nextTick(() => this.$refs.input?.focus())
    },

    /* ── Emit ── */
    emitUpdate() {
      this.$emit('input', { ...this.dataModel, _manual: this.manualMode })
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

.ia-root {
  font-family: 'DM Sans', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

/* ── Labels ── */
.ia-label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9898a6;
  margin-bottom: 6px;
}

.ia-label-sm {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #aaaab6;
  margin-bottom: 4px;
  display: block;
}

.ia-label-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c8102e;
  flex-shrink: 0;
}

.ia-optional {
  font-size: 10px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #c8c8d4;
  margin-left: 2px;
}

/* ── Input ── */
.ia-field {
  display: flex;
  flex-direction: column;
}

.ia-input-wrap {
  display: flex;
  align-items: center;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  background: #fafafa;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  overflow: hidden;
}

.ia-input-wrap.is-focused,
.ia-input-wrap:focus-within {
  border-color: #c8102e;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(200, 16, 46, 0.07);
}

.ia-input-wrap.is-filled {
  border-color: #e8e8ed;
  background: #fff;
}

.ia-input-icon {
  flex-shrink: 0;
  width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c8102e;
  font-size: 12px;
  opacity: 0.7;
}

.ia-input {
  flex: 1;
  padding: 12px 4px 12px 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: #1a1a2e;
  background: transparent;
  border: none;
  outline: none;
  min-width: 0;
}

.ia-input--plain {
  padding: 11px 14px;
  width: 100%;
  box-sizing: border-box;
}

.ia-input::placeholder { color: #c4c4d0; }

.ia-input-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 10px;
  flex-shrink: 0;
}

/* ── Spinner ── */
.ia-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f0f0f4;
  border-top-color: #c8102e;
  border-radius: 50%;
  animation: ia-spin 0.65s linear infinite;
}
@keyframes ia-spin { to { transform: rotate(360deg); } }

.ia-clear-btn {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #f0f0f4;
  color: #9898a6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: background 0.15s, color 0.15s;
}
.ia-clear-btn:hover { background: #c8102e; color: #fff; }

/* ── Dropdown ── */
.ia-dropdown {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05);
  list-style: none;
  margin: 0;
  padding: 5px;
  overflow: hidden;
}

.ia-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.1s;
}

.ia-option.is-active,
.ia-option:hover { background: #fff6f7; }

.ia-option-pin {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #fff1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c8102e;
  font-size: 11px;
  flex-shrink: 0;
}

.ia-option-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.ia-option-main {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ia-option-sub {
  font-size: 11px;
  color: #9898a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Récap ── */
.ia-recap {
  background: #fafafa;
  border: 1.5px solid #e8e8ed;
  border-radius: 12px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.ia-recap-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ia-recap-full { grid-column: span 2; }

.ia-recap-divider {
  grid-column: span 2;
  height: 1px;
  background: #f0f0f4;
}

.ia-recap-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c4c4d0;
}

.ia-recap-value {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
}

/* ── Lien manuel ── */
.ia-manual-link-wrap { text-align: right; }

.ia-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #aaaab6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: color 0.15s;
}
.ia-link:hover { color: #c8102e; }

/* ── Mode manuel ── */
.ia-manual-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.ia-manual-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ia-field--full { grid-column: span 2; }

.ia-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fffbea;
  border: 1.5px solid #fde68a;
  border-radius: 10px;
  font-size: 12px;
  color: #92400e;
}

.ia-error {
  margin: 5px 0 0;
  font-size: 12px;
  color: #c8102e;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── Transitions ── */
.ia-drop-enter-active, .ia-drop-leave-active  { transition: opacity 0.15s, transform 0.15s; transform-origin: top; }
.ia-drop-enter, .ia-drop-leave-to             { opacity: 0; transform: scaleY(0.95) translateY(-4px); }

.ia-reveal-enter-active { transition: opacity 0.2s, transform 0.2s; }
.ia-reveal-enter        { opacity: 0; transform: translateY(-5px); }
</style>